import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../core/redux/store";
import { fetchAllCategories } from "../../../core/redux/categorySlice";
import { contentService } from "../../../core/services/content.service";
import { chatgptService } from "../../../core/services/chatgpt.service";
import { utils } from "../../../core/helpers";
import { ContentSection, LevelGroup, ActiveIndices } from "../types";

const GPT_MODEL = "gpt-4o-realtime-preview-2024-12-17";
const VOICE_MODEL = "alloy";

export const useContentLogic = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.user);
    const { categories, loading } = useSelector(
        (state: RootState) => state.categories
    );
    const { levels } = useSelector((state: RootState) => state.levels);

    const [isLoading, setIsLoading] = useState(true);

    const [sections, setSections] = useState<ContentSection[]>([]);
    const [groupedSectionsByLevel, setGroupedSectionsByLevel] = useState<LevelGroup[]>([]);

    const [successPercentage, setSuccessPercentage] = useState<number>(0);
    const [selectedContent, setSelectedContent] = useState<any | null>(null);
    const [selectedContentUrl, setSelectedContentUrl] = useState<string | null>(null);
    const [activeContentId, setActiveContentId] = useState<number | null>(null);
    const [selectedLevel, setSelectedLevel] = useState<string | null>(null);

    const [selectedLevelIndex, setSelectedLevelIndex] = useState<number | null>(null);

    const [activeContentIndices, setActiveContentIndices] = useState<ActiveIndices | null>(null);

    const [userAnswer, setUserAnswer] = useState("");
    const [title, setTitle] = useState("");
    const [answerResult, setAnswerResult] = useState<any | null>(null);
    const [answerResults, setAnswerResults] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- AI SPEAK STATES (Category 45) ---
    const [aiSession, setAiSession] = useState<any>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiConversation, setAiConversation] = useState<any[]>([]);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [conversation, setConversation] = useState<any[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isChatLoading, setIsChatLoading] = useState(false);

    const chatRequestInProgress = useRef(false);
    const lastProcessedMessageIndex = useRef<number>(-1);

    const activeTopicRef = useRef<number | null>(null);

    const selectedCategoryStr = utils.getStore("selectedCategory");
    const isTopicOnlyMode = String(selectedCategoryStr) === "45";
    const isChatMode = String(selectedCategoryStr) === "46";

    // --- AUTH & NAVIGATION CHECKS ---
    useEffect(() => {
        if (
            !(
                !!utils.getStore("selectedLevel") &&
                !!utils.getStore("selectedCategory")
            )
        ) {
            navigate("/");
            return;
        }
        if (!(!!utils.getPasswordToken() && !!user)) {
            navigate("/login");
            return;
        }
    }, [user, navigate]);

    useEffect(() => {
        if (categories.length === 0 && !loading) {
            dispatch(fetchAllCategories());
        }
    }, [categories, dispatch, loading]);

    useEffect(() => {
        if (selectedContent && selectedContent.content_type_id === 1) {
            contentService.getAnswerResults(selectedContent.id).then((res) => {
                setAnswerResults(res);
            });
        }
    }, [selectedContent]);

    useEffect(() => {
        if (!categories) return;
        if (!utils.getStore("selectedLevel")) return;
        const selectedCategory = utils.getStore("selectedCategory");
        const category = categories.find((x: any) => x.id == selectedCategory);
        // @ts-ignore
        setTitle(levels.find(x => x.value === JSON.parse(utils.getStore("selectedLevel"))[0].toString()).name + (category ? " - " + category.name : ""))
    }, [levels, categories]);

    // --- AI HELPER FUNCTIONS (SPEAKING) ---
    const generateAiInstruction = useCallback((topicName: string, topicDesc: string, levelName: string) => {
        const descriptionPart = topicDesc ? ` ${topicDesc}` : "";
        return `You are an English language tutor focusing on: "${"Act as an English Tutor whose name is Whizzy." + descriptionPart}"
                Follow these rules:
                1. Start the conversation with: "Hello! I'm Whizzy, your English tutor. Let's practice English together." Then list the topics we'll cover.
                2. Keep the conversation focused on the topic: ${topicName} (${levelName})
                3. You MUST ALWAYS respond in English only, regardless of the user's language
                4. If the user speaks in another language, respond with: "I understand you're speaking in [detected language]. However, let's practice in English. Could you try to express yourself in English?"
                5. Keep your responses clear, simple, and suitable for language learners
                6. Focus on encouraging English practice and gentle correction
                7. Never switch to any language other than English under any circumstances
                8. If you detect non-English input multiple times, say: "Remember, this is English practice time! I'll help you express yourself in English."
                9. Always stay within the context of the given topic
                10. Keep responses concise (2-3 sentences) for better conversation flow`;
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data.type === 'SESSION_EXPIRED') console.warn("AI Session Expired");
                if (data.type === "response.audio_transcript.done") {
                    setAiConversation(prev => [...prev, { message: data.transcript, owner: "AI" }]);
                }
                if (data.type === "conversation.item.input_audio_transcription.completed") {
                    setAiConversation(prev => [...prev, { message: data.transcript, owner: "You" }]);
                }
            } catch (e) { }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const initAiSession = async (topic: ContentSection, levelName: string) => {
        if (!isTopicOnlyMode) return;
        setAiLoading(true);
        setAiSession(null);
        setAiConversation([]);
        try {
            const apiKey = await chatgptService.getChatGptKey();
            const instructions = generateAiInstruction(topic.name, topic.description || "", levelName);
            const sessionData = await chatgptService.createNewSession(GPT_MODEL, VOICE_MODEL, instructions, apiKey);
            setAiSession({
                ...sessionData,
                instructions,
                topicName: topic.name,
                levelName: levelName,
                topicDesc: topic.description || ""
            });
        } catch (error) {
            console.error("Failed to start AI session:", error);
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;

        const getChatResponse = async () => {
            try {
                if (chatRequestInProgress.current) return;

                chatRequestInProgress.current = true;

                const lastIndex = conversation.length - 1;
                lastProcessedMessageIndex.current = lastIndex;

                setIsChatLoading(true);

                const apiKey = await chatgptService.getChatGptKey();
                const systemContent = selectedContent?.detail || selectedContent?.title || "English Learning";

                const messagesForApi = conversation.map(item => ({
                    role: item.owner === "You" ? "user" : "assistant",
                    content: item.message
                }));

                const fullMessages = [
                    { role: "system", content: systemContent },
                    ...messagesForApi
                ];

                const response = await chatgptService.sendChat(fullMessages, apiKey);

                if (response.choices && response.choices.length > 0) {
                    const aiMessage = response.choices[0].message.content;

                    setConversation(prev => [
                        ...prev,
                        {
                            owner: "AI",
                            message: aiMessage,
                            isAudio: false
                        }
                    ]);
                }
            } catch (error) {
                console.error("Chat API Error:", error);
            } finally {
                setIsChatLoading(false);
                chatRequestInProgress.current = false;
            }
        };

        if (isChatMode && conversation.length > 0) {
            const lastIndex = conversation.length - 1;
            const lastMessage = conversation[lastIndex];

            if (lastMessage.owner !== "AI" &&
                !chatRequestInProgress.current &&
                lastIndex > lastProcessedMessageIndex.current) {

                const delay = conversation.length === 1 ? 700 : 0;

                timer = setTimeout(() => {
                    getChatResponse();
                }, delay);
            }
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [conversation, isChatMode, selectedContent]);

    const handleChatSend = () => {
        if (!chatInput.trim()) return;
        setConversation(prev => [
            ...prev,
            {
                owner: "You",
                message: chatInput,
                isAudio: false,
                isHidden: false
            }
        ]);
        setChatInput("");
    };

    // --- CONTENT SELECTION HANDLERS ---
    const handleContentSelect = useCallback(
        (
            content: any,
            sectionsForLevel: ContentSection[],
            levelIndex: number,
            sectionIndex: number,
            contentIndex: number
        ) => {
            if (!user?.id) return;
            activeTopicRef.current = null;

            setUserAnswer("");
            setAnswerResult(null);
            setIsSubmitting(false);
            setSelectedContent(content);
            setSections(sectionsForLevel);
            const levelName = groupedSectionsByLevel[levelIndex]?.levelName || "";
            setSelectedLevel(levelName)

            if (content.content_type_id === 2) {
                const userId = user.id;
                const token = utils.getPasswordToken();
                const parameters = `${content.hh5p_content_uuid}?content_id=${content.id}&token=${token}&user_id=${userId}`;
                const url = `${process.env.REACT_APP_API_URL}/interactive/embed/${parameters}`;
                setSelectedContentUrl(url);
            } else {
                setSelectedContentUrl(null);
            }

            setActiveContentId(content.id);
            setActiveContentIndices({ levelIndex, sectionIndex, contentIndex });

            if (content.content_type_id === 1) {
                contentService.getAnswerResults(content.id).then(setAnswerResults);
            }
        },
        [user]
    );



    const handleTopicSelect = (topic: ContentSection, levelIndex: number, sectionIndex: number) => {
        setSelectedLevelIndex(levelIndex)
        if (activeTopicRef.current === topic.id) {
            return;
        }

        activeTopicRef.current = topic.id;

        if (isTopicOnlyMode) {
            const syntheticContent = { id: topic.id, title: topic.name, content_type_id: 999, is_solved: false };
            setSelectedContent(syntheticContent);
            setActiveContentId(topic.id);
            setSelectedContentUrl(null);
            const levelName = groupedSectionsByLevel[levelIndex]?.levelName || "";
            setSelectedLevel(levelName)
            initAiSession(topic, levelName);
        } else if (isChatMode) {
            const syntheticContent = {
                id: topic.id,
                title: topic.name,
                content_type_id: 998,
                is_solved: false,
                detail: topic.description || topic.name
            };
            setSelectedContent(syntheticContent);
            setActiveContentId(topic.id);
            setSelectedContentUrl(null);

            chatRequestInProgress.current = false;
            lastProcessedMessageIndex.current = -1; // Reset so index 0 is valid again

            setConversation([
                {
                    owner: "You",
                    message: "Hi.",
                    isAudio: false,
                    isHidden: true
                }
            ]);
            setChatInput("");
        } else {
            const syntheticContent = { id: topic.id, title: topic.name, content_type_id: 999, detail: `<p>Topic selected: ${topic.name}</p>`, is_solved: false };
            setSelectedContent(syntheticContent);
            setActiveContentId(topic.id);
            setSelectedContentUrl(null);
        }
        const levelName = groupedSectionsByLevel[levelIndex]?.levelName || "";
        setSelectedLevel(levelName)
        setUserAnswer("");
        setAnswerResult(null);
        setActiveContentIndices({ levelIndex, sectionIndex, contentIndex: -1 });
    };

    useEffect(() => {
        const selectedLevelsStr = utils.getStore("selectedLevel");
        const selectedCategory = utils.getStore("selectedCategory");



        if (selectedLevelsStr && selectedCategory && user?.id) {
            setIsLoading(true);
            let levelValues: string[] = [];
            try { levelValues = JSON.parse(selectedLevelsStr); } catch (e) { levelValues = []; }

            if (levelValues.length === 0) {
                setIsLoading(false);
                setGroupedSectionsByLevel([]);
                setSections([]);
                return;
            }

            const levelPromises: Promise<[any, any]>[] = levelValues.map((levelValue: string) =>
                Promise.all([
                    contentService.getTopics(levelValue),
                    (isTopicOnlyMode || isChatMode) ? Promise.resolve([]) : contentService.getAll({ level: levelValue, category: selectedCategory }),
                ])
            );

            Promise.all(levelPromises)
                .then((results) => {
                    let allContents: any[] = [];
                    const newGroupedSectionsByLevel: LevelGroup[] = [];
                    let initialContentToSelect: any = null;
                    let initialTopicToSelect: any = null;

                    results.forEach(([topicsData, contentsData], levelIndex) => {
                        if (!Array.isArray(topicsData) || !Array.isArray(contentsData)) return;

                        const currentLevelValue = levelValues[levelIndex];
                        const levelInfo = levels.find((x: any) => x.value === currentLevelValue);
                        const levelName = levelInfo?.name || `Level ${currentLevelValue}`;

                        allContents = allContents.concat(contentsData);

                        let groupedSections = topicsData.map((topic: any) => {
                            const topicContents = contentsData.filter((content: any) => content.topic_id === topic.id);
                            return { ...topic, contents: topicContents } as ContentSection;
                        });

                        if (!isTopicOnlyMode && !isChatMode) {
                            groupedSections = groupedSections.filter((section: any) => section.contents.length > 0);
                        } else {
                            groupedSections = groupedSections.filter((section: any) => section.description && section.description.trim() !== "");
                        }

                        newGroupedSectionsByLevel.push({
                            levelName,
                            levelValue: currentLevelValue,
                            sections: groupedSections,
                        });

                        if (groupedSections.length > 0) {
                            if (!isTopicOnlyMode && !isChatMode && !initialContentToSelect && groupedSections[0].contents.length > 0) {
                                initialContentToSelect = {
                                    content: groupedSections[0].contents[0],
                                    sections: groupedSections,
                                    levelIndex: levelIndex,
                                    sectionIndex: 0,
                                    contentIndex: 0,
                                };
                            } else if ((isTopicOnlyMode || isChatMode) && !initialTopicToSelect) {
                                initialTopicToSelect = {
                                    topic: groupedSections[0],
                                    levelIndex: levelIndex,
                                    sectionIndex: 0
                                };
                            }
                        }
                    });

                    if (!isTopicOnlyMode && !isChatMode) {
                        const solvedContents = allContents.filter((x: any) => x.is_solved);
                        const percentage = allContents.length > 0 ? (solvedContents.length * 100) / allContents.length : 0;
                        setSuccessPercentage(percentage);
                    } else {
                        setSuccessPercentage(0);
                    }
                    setGroupedSectionsByLevel(newGroupedSectionsByLevel);

                    if (!isTopicOnlyMode && !isChatMode && initialContentToSelect) {
                        handleContentSelect(initialContentToSelect.content, initialContentToSelect.sections, initialContentToSelect.levelIndex, initialContentToSelect.sectionIndex, initialContentToSelect.contentIndex);
                    } else if ((isTopicOnlyMode || isChatMode) && initialTopicToSelect) {
                        handleTopicSelect(initialTopicToSelect.topic, initialTopicToSelect.levelIndex, initialTopicToSelect.sectionIndex);
                        if (newGroupedSectionsByLevel.length > 0) setSections(newGroupedSectionsByLevel[0].sections);
                    } else {
                        setSelectedContent(null);
                        setSelectedContentUrl(null);
                        setSections([]);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching course data:", error);
                    setGroupedSectionsByLevel([]);
                    setSections([]);
                })
                .finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
            setGroupedSectionsByLevel([]);
            setSections([]);
        }
    }, [user, levels, categories, dispatch, handleContentSelect, isTopicOnlyMode, isChatMode]);

    useEffect(() => {
        if (!groupedSectionsByLevel || selectedLevelIndex === null) return;
        const levelName = groupedSectionsByLevel[selectedLevelIndex]?.levelName || "";
        setSelectedLevel(levelName)
    }, [groupedSectionsByLevel]);

    const handleSubmitAnswer = () => {
        setIsSubmitting(true);
        contentService.sendAnswer(selectedContent.id, userAnswer)
            .then((res) => {
                setAnswerResult(res);
                contentService.getAnswerResults(selectedContent.id).then(setAnswerResults);
            })
            .finally(() => setIsSubmitting(false));
    };

    const handleNext = () => {
        if (!activeContentIndices || !groupedSectionsByLevel.length) return;
        const { levelIndex, sectionIndex, contentIndex } = activeContentIndices;

        if (isTopicOnlyMode || isChatMode) {
            const currentLevelGroup = groupedSectionsByLevel[levelIndex];
            const nextSectionIndex = sectionIndex + 1;
            const nextLevelIndex = levelIndex + 1;

            if (nextSectionIndex < currentLevelGroup.sections.length) {
                handleTopicSelect(currentLevelGroup.sections[nextSectionIndex], levelIndex, nextSectionIndex);
            }
            else if (nextLevelIndex < groupedSectionsByLevel.length) {
                const nextLevelGroup = groupedSectionsByLevel[nextLevelIndex];
                if (nextLevelGroup.sections.length > 0) {
                    handleTopicSelect(nextLevelGroup.sections[0], nextLevelIndex, 0);
                }
            }
            return;
        }

        const currentLevelGroup = groupedSectionsByLevel[levelIndex];
        const currentSection = currentLevelGroup.sections[sectionIndex];
        const nextLevelIndex = levelIndex + 1;
        const nextSectionIndex = sectionIndex + 1;
        const nextContentIndex = contentIndex + 1;

        if (nextContentIndex < currentSection.contents.length) {
            const nextContent = currentSection.contents[nextContentIndex];
            handleContentSelect(nextContent, currentLevelGroup.sections, levelIndex, sectionIndex, nextContentIndex);
        } else if (nextSectionIndex < currentLevelGroup.sections.length) {
            const nextSection = currentLevelGroup.sections[nextSectionIndex];
            if (nextSection.contents.length > 0) {
                handleContentSelect(nextSection.contents[0], currentLevelGroup.sections, levelIndex, nextSectionIndex, 0);
            }
        } else if (nextLevelIndex < groupedSectionsByLevel.length) {
            const nextLevelGroup = groupedSectionsByLevel[nextLevelIndex];
            if (nextLevelGroup.sections.length > 0 && nextLevelGroup.sections[0].contents.length > 0) {
                handleContentSelect(nextLevelGroup.sections[0].contents[0], nextLevelGroup.sections, nextLevelIndex, 0, 0);
            }
        }
    };

    const handlePrevious = () => {
        if (!activeContentIndices || !groupedSectionsByLevel.length) return;
        const { levelIndex, sectionIndex, contentIndex } = activeContentIndices;

        if (isTopicOnlyMode || isChatMode) {
            const currentLevelGroup = groupedSectionsByLevel[levelIndex];
            const prevSectionIndex = sectionIndex - 1;
            const prevLevelIndex = levelIndex - 1;

            if (prevSectionIndex >= 0) {
                handleTopicSelect(currentLevelGroup.sections[prevSectionIndex], levelIndex, prevSectionIndex);
            }
            else if (prevLevelIndex >= 0) {
                const prevLevelGroup = groupedSectionsByLevel[prevLevelIndex];
                if (prevLevelGroup.sections.length > 0) {
                    const lastSectionIndex = prevLevelGroup.sections.length - 1;
                    handleTopicSelect(prevLevelGroup.sections[lastSectionIndex], prevLevelIndex, lastSectionIndex);
                }
            }
            return;
        }

        const currentLevelGroup = groupedSectionsByLevel[levelIndex];
        const prevLevelIndex = levelIndex - 1;
        const prevSectionIndex = sectionIndex - 1;
        const prevContentIndex = contentIndex - 1;

        if (prevContentIndex >= 0) {
            const prevContent = currentLevelGroup.sections[sectionIndex].contents[prevContentIndex];
            handleContentSelect(prevContent, currentLevelGroup.sections, levelIndex, sectionIndex, prevContentIndex);
        } else if (prevSectionIndex >= 0) {
            const prevSection = currentLevelGroup.sections[prevSectionIndex];
            if (prevSection.contents.length > 0) {
                const lastContentIndex = prevSection.contents.length - 1;
                handleContentSelect(prevSection.contents[lastContentIndex], currentLevelGroup.sections, levelIndex, prevSectionIndex, lastContentIndex);
            }
        } else if (prevLevelIndex >= 0) {
            const prevLevelGroup = groupedSectionsByLevel[prevLevelIndex];
            if (prevLevelGroup.sections.length > 0) {
                const lastSectionIndex = prevLevelGroup.sections.length - 1;
                const lastSection = prevLevelGroup.sections[lastSectionIndex];
                if (lastSection.contents.length > 0) {
                    const lastContentIndex = lastSection.contents.length - 1;
                    handleContentSelect(lastSection.contents[lastContentIndex], prevLevelGroup.sections, prevLevelIndex, lastSectionIndex, lastContentIndex);
                }
            }
        }
    };

    let isPrevDisabled = true;
    let isNextDisabled = true;
    if (activeContentIndices && groupedSectionsByLevel.length > 0) {
        const { levelIndex, sectionIndex, contentIndex } = activeContentIndices;
        if (isTopicOnlyMode || isChatMode) {
            isPrevDisabled = levelIndex === 0 && sectionIndex === 0;
            const lastLevelIndex = groupedSectionsByLevel.length - 1;
            const lastLevelGroup = groupedSectionsByLevel[lastLevelIndex];
            const lastSectionIndex = lastLevelGroup?.sections?.length - 1;
            isNextDisabled = levelIndex === lastLevelIndex && sectionIndex === lastSectionIndex;
        } else {
            isPrevDisabled = levelIndex === 0 && sectionIndex === 0 && contentIndex === 0;
            const lastLevelIndex = groupedSectionsByLevel.length - 1;
            const lastLevelGroup = groupedSectionsByLevel[lastLevelIndex];
            const lastSectionIndex = lastLevelGroup?.sections?.length - 1;
            const lastContentIndex = lastLevelGroup?.sections[lastSectionIndex]?.contents?.length - 1;
            isNextDisabled = levelIndex === lastLevelIndex && sectionIndex === lastSectionIndex && contentIndex === lastContentIndex;
        }
    }

    return {
        isLoading, title, successPercentage, groupedSectionsByLevel, activeContentId, activeContentIndices,
        selectedContent, selectedContentUrl, isTopicOnlyMode, isChatMode, userAnswer, isSubmitting,
        answerResults, answerResult, aiLoading, aiSession, iframeRef,
        conversation, chatInput, isChatLoading, isPrevDisabled, isNextDisabled, selectedLevel,
        setUserAnswer, setAnswerResult, setAnswerResults, setChatInput,
        handleContentSelect, handleTopicSelect, handleSubmitAnswer, handleNext, handlePrevious, handleChatSend
    };
};